import React, { useRef, useState, useEffect } from 'react';
import { Save, Upload, Link, Cloud, HardDrive, CheckCircle, XCircle } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { uploadWithFeedback, isCloudinaryConfigured } from '../../utils/cloudinaryUpload';

export default function SettingsTab({ 
  settingsForm, 
  setSettingsForm, 
  handleSettingsUpdate,
  newMetalCarat,
  setNewMetalCarat,
  newModelCategory,
  setNewModelCategory,
  newModelName,
  setNewModelName,
  newOtherMaterial,
  setNewOtherMaterial,
  handleAddMetalCarat,
  handleRemoveMetalCarat,
  handleAddModelCategory,
  handleRemoveModelCategory,
  handleAddModelName,
  handleRemoveModelName,
  handleAddOtherMaterial,
  handleRemoveOtherMaterial,
  refreshSettings
}) {
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [cloudinaryEnabled, setCloudinaryEnabled] = useState(false);
  const logoInputRef = useRef(null);
  const heroInputRef = useRef(null);

  // Check Cloudinary status on mount
  useEffect(() => {
    isCloudinaryConfigured().then(setCloudinaryEnabled);
  }, []);

  const handleImageUpload = async (file, type) => {
    if (!file) return;

    if (type === 'logo') setUploadingLogo(true);
    else setUploadingHero(true);

    try {
      const result = await uploadWithFeedback(file, 'site_assets', toast);
      const fullUrl = result.url;
      
      // Update local form state
      const updatedForm = type === 'logo' 
        ? { ...settingsForm, logo_url: fullUrl }
        : { ...settingsForm, hero_image_url: fullUrl };
      setSettingsForm(updatedForm);
      
      // Auto-save to database immediately
      try {
        await api.put('/settings', type === 'logo' ? { logo_url: fullUrl } : { hero_image_url: fullUrl });
        if (refreshSettings) await refreshSettings();
      } catch (saveError) {
        toast.info('Click "Save Settings" to persist.');
      }
    } catch (error) {
    } finally {
      if (type === 'logo') {
        setUploadingLogo(false);
        if (logoInputRef.current) logoInputRef.current.value = '';
      } else {
        setUploadingHero(false);
        if (heroInputRef.current) heroInputRef.current.value = '';
      }
    }
  };

  return (
    <div data-testid="settings-tab">
      <h2 className="font-serif text-2xl font-semibold text-[#1a3a5c] mb-6">Website Settings</h2>
      
      {/* Cloud Storage Status Banner */}
      <div className={`mb-6 p-4 rounded-lg border ${cloudinaryEnabled ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-center gap-3">
          {cloudinaryEnabled ? (
            <>
              <Cloud className="h-5 w-5 text-green-600" />
              <div>
                <p className="font-medium text-green-800">Cloud Storage Enabled</p>
                <p className="text-sm text-green-600">Images are stored permanently on Cloudinary CDN</p>
              </div>
              <CheckCircle className="h-5 w-5 text-green-600 ml-auto" />
            </>
          ) : (
            <>
              <HardDrive className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Local Storage Active</p>
                <p className="text-sm text-amber-600">⚠️ Images will be lost on deployment. Configure Cloudinary below for permanent storage.</p>
              </div>
              <XCircle className="h-5 w-5 text-amber-600 ml-auto" />
            </>
          )}
        </div>
      </div>
      
      <form onSubmit={handleSettingsUpdate} className="bg-white p-8 rounded-lg border border-gray-200 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Link className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={settingsForm.logo_url}
                  onChange={(e) => setSettingsForm({ ...settingsForm, logo_url: e.target.value })}
                  className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                  placeholder="Enter logo URL or upload"
                  data-testid="logo-url-input"
                />
              </div>
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
                className={`px-4 py-3 rounded-sm flex items-center gap-2 transition-colors whitespace-nowrap ${
                  uploadingLogo
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-[#C9A967] hover:bg-[#b8954d] text-white'
                }`}
                data-testid="upload-logo-btn"
              >
                {uploadingLogo ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    <span>Upload</span>
                  </>
                )}
              </button>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={(e) => handleImageUpload(e.target.files?.[0], 'logo')}
                className="hidden"
              />
            </div>
            {settingsForm.logo_url && (
              <div className="mt-3">
                <p className="text-sm text-gray-600 mb-2">Logo Preview:</p>
                <img src={settingsForm.logo_url} alt="Logo preview" className="h-20 w-20 object-contain border border-gray-200 rounded p-2" />
              </div>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Hero Banner Image</label>
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Link className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={settingsForm.hero_image_url}
                  onChange={(e) => setSettingsForm({ ...settingsForm, hero_image_url: e.target.value })}
                  className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                  placeholder="Enter hero image URL or upload"
                  data-testid="hero-image-input"
                />
              </div>
              <button
                type="button"
                onClick={() => heroInputRef.current?.click()}
                disabled={uploadingHero}
                className={`px-4 py-3 rounded-sm flex items-center gap-2 transition-colors whitespace-nowrap ${
                  uploadingHero
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-[#C9A967] hover:bg-[#b8954d] text-white'
                }`}
                data-testid="upload-hero-btn"
              >
                {uploadingHero ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    <span>Upload</span>
                  </>
                )}
              </button>
              <input
                ref={heroInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={(e) => handleImageUpload(e.target.files?.[0], 'hero')}
                className="hidden"
              />
            </div>
            {settingsForm.hero_image_url && (
              <div className="mt-3">
                <p className="text-sm text-gray-600 mb-2">Banner Preview:</p>
                <img src={settingsForm.hero_image_url} alt="Hero preview" className="h-32 w-full object-cover rounded border border-gray-200" />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email</label>
            <input
              type="email"
              value={settingsForm.contact_email}
              onChange={(e) => setSettingsForm({ ...settingsForm, contact_email: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="support@example.com"
              required
              data-testid="contact-email-input"
            />
            <p className="text-xs text-gray-500 mt-1">Admin notifications will be sent to this email</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contact Phone</label>
            <input
              type="tel"
              value={settingsForm.contact_phone}
              onChange={(e) => setSettingsForm({ ...settingsForm, contact_phone: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="+91 98765 43210"
              required
              data-testid="contact-phone-input"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Website URL</label>
            <input
              type="url"
              value={settingsForm.site_url || ''}
              onChange={(e) => setSettingsForm({ ...settingsForm, site_url: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="https://marissa.in"
              data-testid="site-url-input"
            />
            <p className="text-xs text-gray-500 mt-1">Used for product links in customer emails (e.g., payment reminder emails)</p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Contact Address</label>
            <input
              type="text"
              value={settingsForm.contact_address}
              onChange={(e) => setSettingsForm({ ...settingsForm, contact_address: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="City, Country"
              required
              data-testid="contact-address-input"
            />
          </div>

          <div className="md:col-span-2 pt-4 border-t border-gray-200">
            <h3 className="font-serif text-lg font-semibold text-[#1a3a5c] mb-4">Social Media Links</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Facebook URL</label>
            <input
              type="url"
              value={settingsForm.facebook_url}
              onChange={(e) => setSettingsForm({ ...settingsForm, facebook_url: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="https://facebook.com/yourpage"
              data-testid="facebook-url-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Instagram URL</label>
            <input
              type="url"
              value={settingsForm.instagram_url}
              onChange={(e) => setSettingsForm({ ...settingsForm, instagram_url: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="https://instagram.com/yourpage"
              data-testid="instagram-url-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Twitter URL</label>
            <input
              type="url"
              value={settingsForm.twitter_url}
              onChange={(e) => setSettingsForm({ ...settingsForm, twitter_url: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="https://twitter.com/yourpage"
              data-testid="twitter-url-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp Business Number</label>
            <input
              type="tel"
              value={settingsForm.whatsapp_number}
              onChange={(e) => setSettingsForm({ ...settingsForm, whatsapp_number: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="+919876543210 (with country code)"
              data-testid="whatsapp-number-input"
            />
            <p className="text-xs text-gray-500 mt-1">Used for order notifications to customers</p>
          </div>

          <div className="md:col-span-2 pt-4 border-t border-gray-200">
            <h3 className="font-serif text-lg font-semibold text-[#1a3a5c] mb-4">Payment Gateway Settings</h3>
            <p className="text-sm text-gray-600 mb-4">Configure your payment gateway. You can use Razorpay, PhonePe, or both.</p>
          </div>

          {/* Active Payment Gateway Selection */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Active Payment Gateway</label>
            <select
              value={settingsForm.active_payment_gateway || 'razorpay'}
              onChange={(e) => setSettingsForm({ ...settingsForm, active_payment_gateway: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              data-testid="active-gateway-select"
            >
              <option value="razorpay">Razorpay</option>
              <option value="phonepe">PhonePe</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Select which payment gateway to use for checkout</p>
          </div>

          {/* Razorpay Settings */}
          <div className="md:col-span-2 pt-3">
            <h4 className="font-medium text-[#1a3a5c] mb-3 flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${settingsForm.active_payment_gateway === 'razorpay' ? 'bg-green-500' : 'bg-gray-300'}`}></span>
              Razorpay Configuration
            </h4>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Razorpay Key ID</label>
            <input
              type="text"
              value={settingsForm.razorpay_key_id || ''}
              onChange={(e) => setSettingsForm({ ...settingsForm, razorpay_key_id: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="rzp_test_xxxxxxxxxxxxx or rzp_live_xxxxxxxxxxxxx"
              data-testid="razorpay-key-id-input"
            />
            <p className="text-xs text-gray-500 mt-1">Get from Razorpay Dashboard → Settings → API Keys</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Razorpay Key Secret</label>
            <input
              type="password"
              value={settingsForm.razorpay_key_secret || ''}
              onChange={(e) => setSettingsForm({ ...settingsForm, razorpay_key_secret: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="Enter your Razorpay secret key"
              data-testid="razorpay-secret-input"
            />
          </div>

          {/* PhonePe Settings */}
          <div className="md:col-span-2 pt-3">
            <h4 className="font-medium text-[#1a3a5c] mb-3 flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${settingsForm.active_payment_gateway === 'phonepe' ? 'bg-green-500' : 'bg-gray-300'}`}></span>
              PhonePe Configuration
            </h4>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">PhonePe Environment</label>
            <select
              value={settingsForm.phonepe_env || 'sandbox'}
              onChange={(e) => setSettingsForm({ ...settingsForm, phonepe_env: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              data-testid="phonepe-env-select"
            >
              <option value="sandbox">Sandbox (Testing)</option>
              <option value="production">Production (Live)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Use Sandbox for testing, Production for live payments</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">PhonePe Merchant ID</label>
            <input
              type="text"
              value={settingsForm.phonepe_merchant_id || ''}
              onChange={(e) => setSettingsForm({ ...settingsForm, phonepe_merchant_id: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="Enter your PhonePe Merchant ID"
              data-testid="phonepe-merchant-id-input"
            />
            <p className="text-xs text-gray-500 mt-1">Get from PhonePe Business Dashboard</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">PhonePe Salt Key</label>
            <input
              type="password"
              value={settingsForm.phonepe_salt_key || ''}
              onChange={(e) => setSettingsForm({ ...settingsForm, phonepe_salt_key: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="Enter your PhonePe Salt Key"
              data-testid="phonepe-salt-key-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">PhonePe Salt Index</label>
            <input
              type="text"
              value={settingsForm.phonepe_salt_index || '1'}
              onChange={(e) => setSettingsForm({ ...settingsForm, phonepe_salt_index: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="1"
              data-testid="phonepe-salt-index-input"
            />
            <p className="text-xs text-gray-500 mt-1">Usually &quot;1&quot; unless specified otherwise</p>
          </div>

          {/* Metal Carat Options */}
          <div className="md:col-span-2 pt-4 border-t border-gray-200">
            <h3 className="font-serif text-lg font-semibold text-[#1a3a5c] mb-4">Metal Carat Options</h3>
            <div className="flex flex-wrap gap-2 mb-4">
              {settingsForm.metal_carats?.map((carat) => (
                <span key={carat} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-[#C9A967]/20 text-[#1a3a5c]">
                  {carat}
                  <button
                    type="button"
                    onClick={() => handleRemoveMetalCarat(carat)}
                    className="ml-2 text-red-500 hover:text-red-700"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newMetalCarat}
                onChange={(e) => setNewMetalCarat(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                placeholder="e.g., 24k"
                data-testid="new-carat-input"
              />
              <button
                type="button"
                onClick={handleAddMetalCarat}
                className="px-4 py-2 bg-[#1a3a5c] text-white rounded-sm hover:bg-[#1f4268]"
                data-testid="add-carat-button"
              >
                Add
              </button>
            </div>
          </div>

          {/* Model Category Options - Managed from Homepage tab */}
          <div className="md:col-span-2 pt-4 border-t border-gray-200">
            <h3 className="font-serif text-lg font-semibold text-[#1a3a5c] mb-2">Model Category Options</h3>
            <p className="text-sm text-gray-500 mb-3">Managed from <strong>Homepage → Shop by Category</strong> tab. Add/remove categories there.</p>
            <div className="flex flex-wrap gap-2">
              {settingsForm.model_categories?.map((category) => (
                <span key={category} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-[#C9A967]/20 text-[#1a3a5c]">
                  {category}
                </span>
              ))}
            </div>
          </div>

          {/* Model Names - Managed from Homepage tab */}
          <div className="md:col-span-2 pt-4 border-t border-gray-200">
            <h3 className="font-serif text-lg font-semibold text-[#1a3a5c] mb-2">Model Names</h3>
            <p className="text-sm text-gray-500 mb-3">Managed from <strong>Homepage → Collections</strong> tab. Add/remove collections there.</p>
            <div className="flex flex-wrap gap-2">
              {settingsForm.model_names?.map((name) => (
                <span key={name} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                  {name}
                </span>
              ))}
            </div>
          </div>

          {/* Other Material Types */}
          <div className="md:col-span-2 pt-4 border-t border-gray-200">
            <h3 className="font-serif text-lg font-semibold text-[#1a3a5c] mb-4">Other Material Types</h3>
            <p className="text-sm text-gray-600 mb-3">Materials like steel, rubber, leather used in jewelry products</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {settingsForm.other_material_types?.map((material) => (
                <span key={material} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-200 text-[#1a3a5c]">
                  {material}
                  <button
                    type="button"
                    onClick={() => handleRemoveOtherMaterial(material)}
                    className="ml-2 text-red-500 hover:text-red-700"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newOtherMaterial || ''}
                onChange={(e) => setNewOtherMaterial(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                placeholder="e.g., Ceramic, Wood"
                data-testid="new-other-material-input"
              />
              <button
                type="button"
                onClick={handleAddOtherMaterial}
                className="px-4 py-2 bg-[#1a3a5c] text-white rounded-sm hover:bg-[#1f4268]"
                data-testid="add-other-material-button"
              >
                Add
              </button>
            </div>
          </div>

          {/* Cloudinary Cloud Storage Settings */}
          <div className="md:col-span-2 pt-4 border-t border-gray-200">
            <h3 className="font-serif text-lg font-semibold text-[#1a3a5c] mb-4 flex items-center gap-2">
              <Cloud className="h-5 w-5" />
              Cloud Storage (Cloudinary)
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Configure Cloudinary for permanent image storage. Get free credentials from{' '}
              <a href="https://cloudinary.com" target="_blank" rel="noopener noreferrer" className="text-[#1a3a5c] underline">
                cloudinary.com
              </a>
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cloudinary Cloud Name</label>
            <input
              type="text"
              value={settingsForm.cloudinary_cloud_name || ''}
              onChange={(e) => setSettingsForm({ ...settingsForm, cloudinary_cloud_name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="Your cloud name from Cloudinary dashboard"
              data-testid="cloudinary-cloud-name-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cloudinary API Key</label>
            <input
              type="text"
              value={settingsForm.cloudinary_api_key || ''}
              onChange={(e) => setSettingsForm({ ...settingsForm, cloudinary_api_key: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="API Key from Cloudinary dashboard"
              data-testid="cloudinary-api-key-input"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Cloudinary API Secret</label>
            <input
              type="password"
              value={settingsForm.cloudinary_api_secret || ''}
              onChange={(e) => setSettingsForm({ ...settingsForm, cloudinary_api_secret: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
              placeholder="API Secret from Cloudinary dashboard"
              data-testid="cloudinary-api-secret-input"
            />
            <p className="text-xs text-gray-500 mt-1">This is kept secure and never exposed to the frontend</p>
          </div>
        </div>

        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            type="submit"
            className="bg-[#1a3a5c] text-white px-8 py-3 rounded-full hover:bg-[#0d2240] transition-colors flex items-center space-x-2"
            data-testid="save-settings-button"
          >
            <Save className="h-5 w-5" />
            <span>Save Settings</span>
          </button>
        </div>
      </form>
    </div>
  );
}
